import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  runTransaction,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Quote, QuoteItem, ID, TaxCalcMode } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot } from '@shared/utils';
import { generateCode, generateBranchNumber, writeAuditLog } from '@shared/utils';
import { fetchCustomer } from '@modules/customer';
import { nextMonthTitle, shiftMonthTokens, shiftTimestampOneMonth } from '@modules/order/utils/month-shift';

const stampFlagsSchema = z.object({
  tantou: z.boolean().default(false),
  kakunin: z.boolean().default(false),
  kaisha: z.boolean().default(false),
});

const quoteCreateSchema = z.object({
  customerId: z.string().min(1),
  contactId: z.string().nullable().default(null),
  orderId: z.string().nullable().default(null),
  title: z.string().min(1, '件名は必須です'),
  validUntil: z.custom<import('firebase/firestore').Timestamp>((v) => v != null && typeof (v as Record<string, unknown>)['seconds'] === 'number'),
  notes: z.string().nullable().default(null),
  internalNotes: z.string().nullable().default(null),
  assignedUserId: z.string().min(1),
  templateId: z.string().nullable().default(null),
  // B4追加フィールド
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  advance: z.number().min(0).finite().default(0),
  totalgst: z.number().min(0).finite().default(0),
  grandtotal: z.number().min(0).finite().default(0),
  disabled: z.boolean().default(false),
  // 案件帳票の正式番号「受注番号-枝番」(0001-01)。UI が採番して渡す。
  code: z.string().nullable().default(null),
  samemaster: z.boolean().default(false),
  chiefEmployerId: z.string().nullable().default(null),
  atenaType: z.enum(['company_honorific', 'dept_honorific', 'contact_sama']).default('company_honorific'),
  paymentTerm: z.string().default(''),
  businessTypeId: z.string().nullable().default(null),
  usergroupId: z.string().nullable().default(null),
  stampFlags: stampFlagsSchema.default({ tantou: false, kakunin: false, kaisha: true }),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
});

const quoteUpdateSchema = quoteCreateSchema.partial();

export type QuoteCreateInput = z.infer<typeof quoteCreateSchema>;
export type QuoteUpdateInput = Partial<Omit<QuoteCreateInput, 'customerId'>>;

const quoteItemInputSchema = z.object({
  productId: z.string().nullable().default(null),
  // 空行（スペーサー行）を帳票レイアウト用に保存できるよう、名称・単位・数量は空/0を許容する。
  // 入力途中の不正行はフォーム側（useQuoteForm.handleSubmit）で検証する。
  name: z.string().default(''),
  description: z.string().nullable().default(null),
  quantity: z.number().min(0),
  unit: z.string().default(''),
  // 値引き行に対応するため負の単価を許容する（Infinity/NaN は拒否）
  unitPrice: z.number().finite(),
  discountRate: z.number().min(0).max(100).default(0),
  taxType: z.enum(['standard', 'reduced', 'exempt']),
  sortOrder: z.number().int().min(0),
});

export type QuoteItemInput = z.infer<typeof quoteItemInputSchema>;

const TAX_RATES = { standard: 0.1, reduced: 0.08, exempt: 0 } as const;

function roundByMode(value: number, mode: TaxCalcMode): number {
  if (mode === 'ceil') return Math.ceil(value);
  if (mode === 'round') return Math.round(value);
  return Math.floor(value);
}

function calcItem(item: QuoteItemInput, taxCalcMode: TaxCalcMode = 'floor') {
  const subtotal = roundByMode(item.unitPrice * item.quantity * (1 - item.discountRate / 100), taxCalcMode);
  const taxAmount = roundByMode(subtotal * TAX_RATES[item.taxType], taxCalcMode);
  return { subtotal, taxAmount, total: subtotal + taxAmount };
}

function sumItems(items: QuoteItemInput[], taxCalcMode: TaxCalcMode = 'floor') {
  return items.reduce(
    (acc, item) => {
      const c = calcItem(item, taxCalcMode);
      return { subtotal: acc.subtotal + c.subtotal, taxAmount: acc.taxAmount + c.taxAmount, total: acc.total + c.total };
    },
    { subtotal: 0, taxAmount: 0, total: 0 }
  );
}

function buildPartnerSnapshot(customer: Awaited<ReturnType<typeof fetchCustomer>>) {
  if (!customer) return null;
  return {
    name: customer.name,
    furi: customer.nameKana,
    masterCode: customer.code,
    zip: customer.postalCode,
    prefecture: customer.prefecture,
    addr1: customer.addr1,
    addr2: customer.addr2,
    addr3: customer.addr3,
    organize: customer.department ?? '',
    tel: customer.phone,
    ...(customer.tel2 ? { tel2: customer.tel2 } : {}),
  };
}

const COL = 'quotes';
const itemsCol = (quoteId: ID) => collection(db, COL, quoteId, 'items');

export async function fetchQuotes(filters?: {
  customerId?: ID;
  orderId?: ID;
  assignedUserId?: ID;
  includeDisabled?: boolean;
}): Promise<Quote[]> {
  let q = query(collection(db, COL), where('isDeleted', '==', false), orderBy('createdAt', 'desc'));
  if (filters?.customerId) q = query(q, where('customerId', '==', filters.customerId));
  if (filters?.orderId) q = query(q, where('orderId', '==', filters.orderId));
  if (filters?.assignedUserId) q = query(q, where('assignedUserId', '==', filters.assignedUserId));
  if (!filters?.includeDisabled) q = query(q, where('disabled', '==', false));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Quote);
}

export async function fetchQuote(id: ID): Promise<Quote | null> {
  return fromSnapshot<Quote>(await getDoc(doc(db, COL, id)));
}

export async function fetchQuoteItems(quoteId: ID): Promise<QuoteItem[]> {
  const q = query(itemsCol(quoteId), where('isDeleted', '==', false), orderBy('sortOrder'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as QuoteItem);
}

export async function createQuote(data: QuoteCreateInput, userId: ID): Promise<Quote> {
  const validated = parseOrThrow(quoteCreateSchema, data);
  // 案件帳票は UI が採番した「受注番号-枝番」(0001-01) を code に持つ。
  // 案件に紐づかない単独作成のフォールバックのみ PREFIX-00001 を採番する。
  const code = validated.code ?? (await generateCode('QT'));
  const customer = await fetchCustomer(validated.customerId);
  const partnerSnapshot = buildPartnerSnapshot(customer);
  const payload = {
    ...validated, code, version: 1, parentQuoteId: null,
    subtotal: 0, taxAmount: 0, total: 0,
    partnerSnapshot,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({ entityType: 'quote', entityId: ref.id, action: 'create', userId, after: validated });
  return { id: ref.id, ...payload } as unknown as Quote;
}

/**
 * 案件（受注）内で次に付与する枝番「受注番号-連番」を算出する唯一の窓口。
 * - 枝番は案件単位の単純追番。修正版・追加作業・月額分割を区別せず、案件内の既存見積
 *   （無効=disabled 含む）の枝番末尾の最大 + 1 を採番する（1本目は受注番号-1）。
 * - 親番号（受注番号）は orderNo 引数を優先。省略時は案件内の既存枝番から導出する
 *   （版作成・複写など既存見積を起点とする経路で使用）。案件内に枝番付き見積が無い
 *   （= 最初の見積）場合は orderNo が必須。
 */
export async function nextOrderQuoteBranch(orderId: ID, orderNo?: string): Promise<string> {
  const existing = await fetchQuotes({ orderId, includeDisabled: true });
  let maxSeq = 0;
  let parentNo: string | null = orderNo ?? null;
  for (const q of existing) {
    if (!q.code) continue;
    const dash = q.code.lastIndexOf('-');
    if (dash < 0) continue;
    if (parentNo === null) parentNo = q.code.slice(0, dash);
    const seq = parseInt(q.code.slice(dash + 1), 10);
    if (!Number.isNaN(seq) && seq > maxSeq) maxSeq = seq;
  }
  if (parentNo === null) {
    throw new Error('枝番の採番には受注番号が必要です。');
  }
  return generateBranchNumber(parentNo, maxSeq + 1);
}

/** 「受注番号-枝番」から親番号（受注番号）を取り出す。例: "0001-03" → "0001" */
function parentNoOfBranch(code: string | null): string | undefined {
  if (!code) return undefined;
  const dash = code.lastIndexOf('-');
  return dash < 0 ? undefined : code.slice(0, dash);
}

export async function createQuoteVersion(originalId: ID, userId: ID): Promise<Quote> {
  // 採番は Tx 外で先に確定する（generateCode/fetchQuotes が内部で別 Tx/getDocs を使い、
  // 外側 Tx と入れ子にできないため）。枝番は案件内の単純追番で統一する。
  const original = await fetchQuote(originalId);
  if (!original) throw new Error('見積が見つかりません。');
  if (!original.orderId) throw new Error('案件に紐づかない見積からは版を作成できません。');
  const code = await nextOrderQuoteBranch(
    original.orderId,
    parentNoOfBranch(original.code),
  );

  const result = await runTransaction(db, async (tx) => {
    const origRef = doc(db, COL, originalId);
    const origSnap = await tx.get(origRef);
    if (!origSnap.exists()) throw new Error('見積が見つかりません。');
    const current = { id: origSnap.id, ...origSnap.data() } as Quote;

    // 元版を無効化
    tx.update(origRef, { disabled: true, ...buildUpdateFields(userId) });

    // 新版ドキュメントをアトミックに作成
    const newRef = doc(collection(db, COL));
    const { id: _id, ...rest } = current;
    const payload = {
      ...rest,
      code,
      version: (current.version ?? 1) + 1,
      parentQuoteId: originalId,
        disabled: false,
      ...buildBaseFields(userId),
    };
    tx.set(newRef, payload);
    return { id: newRef.id, ...payload };
  });

  // 明細（subcollection）を新版へコピーする
  const items = await fetchQuoteItems(originalId);
  if (items.length > 0) {
    const batch = writeBatch(db);
    const col = itemsCol(result.id);
    for (const item of items) {
      const { id: _itemId, ...itemRest } = item;
      batch.set(doc(col), { ...itemRest, quoteId: result.id, ...buildBaseFields(userId) });
    }
    await batch.commit();
  }

  await writeAuditLog({
    entityType: 'quote',
    entityId: result.id,
    action: 'create',
    userId,
    after: { versionOf: originalId, code: result.code },
  });
  return result as unknown as Quote;
}

/**
 * 見積を明細ごと複写し、案件内の次の番号「受注番号-枝番」(0001-01) を採番した新しい見積を作成する。
 * - 採番は nextOrderQuoteBranch に集約（複写・追加作業も版作成と同じ単純追番）。
 * - 採番した「受注番号-枝番」を code に格納する（旧 generateCode('QT') 採番は廃止）。
 * - 元見積は変更しない（無効化しない）。複写は有効(disabled=false)な見積として作成する。
 */
export async function copyQuoteAsBranch(sourceQuoteId: ID, userId: ID): Promise<{ id: ID }> {
  const source = await fetchQuote(sourceQuoteId);
  if (!source) throw new Error('見積が見つかりません。');
  if (!source.orderId) throw new Error('案件に紐づかない見積は複写できません。');
  const code = await nextOrderQuoteBranch(
    source.orderId,
    parentNoOfBranch(source.code),
  );
  const items = await fetchQuoteItems(sourceQuoteId);

  const { id: _id, ...rest } = source;
  const payload = {
    ...rest,
    title: `${source.title} コピー`,
    code,
    parentQuoteId: source.parentQuoteId ?? sourceQuoteId,
    disabled: false,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);

  if (items.length > 0) {
    const batch = writeBatch(db);
    const col = itemsCol(ref.id);
    for (const item of items) {
      const { id: _itemId, ...itemRest } = item;
      batch.set(doc(col), { ...itemRest, quoteId: ref.id, ...buildBaseFields(userId) });
    }
    await batch.commit();
  }

  await writeAuditLog({
    entityType: 'quote',
    entityId: ref.id,
    action: 'create',
    userId,
    after: { copiedFrom: sourceQuoteId, code },
  });
  return { id: ref.id };
}

/**
 * 見積を明細ごと複製する（受注複製に付随して呼ばれる）。
 * - 新規採番・version=1 の独立した見積として作成する。
 * - shiftMonth=true の場合、見積タイトルと各明細の name / description の月表記を翌月へシフトし、
 *   有効期限も1ヶ月後へずらす（毎月の保守費用などの翌月分を自動生成する用途）。
 * - shiftMonth=false の場合はタイトルに「 コピー」を付けた単純複製。
 * - orderId / code を渡すと、その案件・枝番に紐づいた状態で作成する。作成後に貼り替える方式だと
 *   途中で失敗したときに複製元の案件へ紛れ込んだ見積が残るため、連結先が決まっているなら必ず渡す。
 *   省略時は複製元の案件をそのまま引き継ぎ、code は PREFIX-00001 形式で新規採番する。
 */
export async function duplicateQuoteWithItems(
  sourceQuoteId: ID,
  userId: ID,
  options: { shiftMonth: boolean; orderId?: ID; code?: string }
): Promise<{ id: ID; grandtotal: number }> {
  const source = await fetchQuote(sourceQuoteId);
  if (!source) throw new Error('見積が見つかりません。');
  const items = await fetchQuoteItems(sourceQuoteId);

  const code = options.code ?? (await generateCode('QT'));
  const { id: _id, ...rest } = source;
  const title = options.shiftMonth ? nextMonthTitle(source.title) : `${source.title} コピー`;
  const validUntil = options.shiftMonth ? shiftTimestampOneMonth(source.validUntil) : source.validUntil;

  const payload = {
    ...rest,
    ...(options.orderId ? { orderId: options.orderId } : {}),
    code,
    title,
    validUntil,
    version: 1,
    parentQuoteId: null,
    disabled: false,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);

  if (items.length > 0) {
    const batch = writeBatch(db);
    const col = itemsCol(ref.id);
    for (const item of items) {
      const { id: _itemId, ...itemRest } = item;
      const name = options.shiftMonth ? shiftMonthTokens(item.name).text : item.name;
      const description =
        options.shiftMonth && item.description
          ? shiftMonthTokens(item.description).text
          : item.description;
      batch.set(doc(col), { ...itemRest, name, description, quoteId: ref.id, ...buildBaseFields(userId) });
    }
    await batch.commit();
  }

  await writeAuditLog({
    entityType: 'quote',
    entityId: ref.id,
    action: 'create',
    userId,
    after: { duplicatedFrom: sourceQuoteId, shiftMonth: options.shiftMonth, itemCount: items.length },
  });
  // 月シフトでも金額は変わらないため、複製元の税込合計をそのまま返す（受注の見積額へ反映する用途）
  return { id: ref.id, grandtotal: source.grandtotal ?? 0 };
}

export async function updateQuote(id: ID, data: QuoteUpdateInput, userId: ID): Promise<Quote> {
  const validated = parseOrThrow(quoteUpdateSchema, data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('見積が見つかりません。');
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({ entityType: 'quote', entityId: id, action: 'update', userId, before: snap.data(), after: validated });
  return { id, ...snap.data(), ...patch } as unknown as Quote;
}

export async function upsertQuoteItems(
  quoteId: ID,
  items: QuoteItemInput[],
  userId: ID,
  options?: { advance?: number; taxCalcMode?: TaxCalcMode }
): Promise<QuoteItem[]> {
  const batch = writeBatch(db);
  const col = itemsCol(quoteId);
  const taxCalcMode = options?.taxCalcMode ?? 'floor';
  const advance = options?.advance ?? 0;

  const existingSnap = await getDocs(col);
  existingSnap.docs.forEach((d) => batch.delete(d.ref));

  const saved: QuoteItem[] = [];
  for (const item of items) {
    // parseOrThrow は zod の Input 型（デフォルト適用前）と Output 型の両立を要求するため、
    // デフォルト適用済みの Output 型として明示する（実行時の値は zod が既に検証・補完済み）。
    const validated = parseOrThrow(quoteItemInputSchema, item) as QuoteItemInput;
    const calc = calcItem(validated, taxCalcMode);
    const ref = doc(col);
    const payload = { ...validated, ...calc, quoteId, ...buildBaseFields(userId) };
    batch.set(ref, payload);
    saved.push({ id: ref.id, ...payload } as unknown as QuoteItem);
  }

  const totals = sumItems(items, taxCalcMode);
  const totalgst = totals.taxAmount;
  const grandtotal = totals.subtotal + advance + totalgst;

  batch.update(doc(db, COL, quoteId), {
    ...totals,
    totalgst,
    grandtotal,
    ...buildUpdateFields(userId),
  });

  await batch.commit();
  await writeAuditLog({
    entityType: 'quote_items', entityId: quoteId, action: 'update', userId,
    after: { count: items.length, ...totals, totalgst, grandtotal },
  });
  return saved;
}

export async function deleteQuote(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('見積が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({ entityType: 'quote', entityId: id, action: 'delete', userId, before: snap.data() });
}

